import React, { useEffect, useMemo, useState } from 'react';
import { useModuleConfig } from '@/hooks/queries/admin/useModuleConfig';
import type { IemModuleConfig } from '@/types/moduleConfig';
import { useIeKitLabels } from '@/hooks/useIeKitLabels';
import { Box, Card, CardContent, Tab, Tabs } from '@mui/material';
import { AllInboxOutlined, HistoryOutlined, Inventory2Outlined } from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { IeTable } from '@/components/iem/tables/IeTable';
import { AllocationTab } from '@/components/iem/tabs/AllocationTab';
import { TransactionHistoryTab } from '@/components/iem/tabs/TransactionHistoryTab';
import { IemContextualActions } from '@/components/iem/actions/IemContextualActions';
import { TabActionsContext } from '@/components/iem/panels/TabActionsContext';
import { useAuth } from '@/hooks/useAuth';
import { useIemFrontDoorStore } from '@/stores/iemFrontDoorStore';
import { useQuery } from '@tanstack/react-query';
import { httpClient } from '@/utils/httpClient';
import { API_CONFIG } from '@/config/api';
import {
  FrontWindowShelfMapLauncher,
  type FrontWindowShelfMapHandReceiptContext,
} from '@/components/shelf-map/FrontWindowShelfMapViewer';
import { useRootLocations } from '@/hooks/queries/shelf-map/useShelfMapLayouts';
import { usePHRItems, useTHRItems } from '@/hooks/queries/iem';

export type TabKey = 'allocation' | 'ie' | 'transaction-history';

const tabKeys: TabKey[] = ['allocation', 'ie', 'transaction-history'];

// a11y helpers for Tab/TabPanel linkage
const a11yProps = (name: TabKey, index: number) => ({
  id: `iem-tab-${index}-${name}`,
  'aria-controls': `iem-tabpanel-${index}-${name}`,
});

type TabPanelProps = {
  children?: React.ReactNode;
  value: number;
  index: number;
  name: TabKey;
};

const TabPanel: React.FC<TabPanelProps> = ({ children, value, index, name }) => {
  const isActive = value === index;
  return (
    <div
      role="tabpanel"
      hidden={!isActive}
      id={`iem-tabpanel-${index}-${name}`}
      aria-labelledby={`iem-tab-${index}-${name}`}
    >
      {isActive && <Box sx={{ pt: 2 }}>{children}</Box>}
    </div>
  );
};

const routeForTab = (key: TabKey) =>
  key === 'allocation' ? '/customer-service' : `/customer-service/${key}`;

export const MainContentTabs: React.FC = () => {
  const { config: iemConfig } = useModuleConfig<IemModuleConfig>('iem');
  const iemLabels = iemConfig.features.front_window;
  const { tabLabel: ieKitTabLabel } = useIeKitLabels('front_window');
  const { isServiceMember, isComptroller, isHr, isAuthenticated, hasRole } = useAuth();
  const currentCustomer = useIemFrontDoorStore((s) => s.currentCustomer);

  // Fetch the person_ie_kit entity for the current customer to get their
  // BOI-linked warehouse location. The warehouse_location_id in entity_data is
  // the root location entity ID that drives the shelf map layout selector.
  const { data: personIeKitWarehouseId } = useQuery<string | null>({
    queryKey: ['person-ie-kit-warehouse', currentCustomer?.id],
    queryFn: async () => {
      if (!currentCustomer?.id) return null;
      const result = await httpClient.get<
        Array<{ entity_data: { warehouse_location_id?: string | null } | null }>
      >(
        `${API_CONFIG.ENDPOINTS.ENTITIES}?entity_type=eq.person_ie_kit&is_current=eq.true&entity_data->>person_id=eq.${currentCustomer.id}&select=entity_data&limit=1`,
      );
      return result[0]?.entity_data?.warehouse_location_id ?? null;
    },
    enabled: isAuthenticated && !!currentCustomer?.id,
    staleTime: 30_000,
  });

  // Fallback: when no person-specific warehouse is linked, auto-select the
  // first available root location (storefront warehouse) so the Front Window
  // shelf map launcher is enabled by default (#457).
  const { data: rootLocations = [] } = useRootLocations();
  // Prefer the root location whose entity_identifier matches the storefront
  // convention (contains 'ROOT-SF'). Fall back to the first alphabetically.
  const storefrontLocation =
    rootLocations.find((loc) => loc.entity_identifier.toUpperCase().includes('ROOT-SF')) ??
    rootLocations[0];
  const defaultWarehouseId = storefrontLocation?.id ?? null;
  const defaultWarehouseName = storefrontLocation?.display_name ?? null;

  // Resolved warehouse: prefer the customer's IE-kit warehouse; fall back to
  // the first root location when none is set.
  const resolvedWarehouseId = personIeKitWarehouseId ?? defaultWarehouseId;
  const resolvedWarehouseName = personIeKitWarehouseId ? null : defaultWarehouseName;

  // Fetch active THR and PHR items for the current customer so we can pass
  // hand receipt context to the shelf map launcher when either receipt type
  // is loaded (#912, #1086). Both temporary (THR) and permanent (PHR) hand
  // receipts are valid "hand receipt item context" sources — the Allocation
  // tab surfaces both (see AllocationTab's 'phr' | 'thr' source union), and
  // PHR is the standing/primary receipt for most issued gear, so scoping to
  // THR alone left PHR-only customers viewing an unscoped, full-site map.
  // This fetch is lightweight (same queries AllocationTab already makes) and
  // is enabled only when a customer is loaded.
  const { data: thrItems = [] } = useTHRItems(
    currentCustomer?.id ?? '',
    { status: 'neq.closed' },
    isAuthenticated && !!currentCustomer?.id,
  );
  const { data: phrItems = [] } = usePHRItems(
    currentCustomer?.id ?? '',
    { status: 'neq.closed' },
    isAuthenticated && !!currentCustomer?.id,
  );

  // Build hand receipt context when one or more active THR/PHR items exist.
  // The context indicator shows the customer name and item count in the viewer
  // header to confirm the user is viewing a receipt-scoped warehouse view.
  // `lins` drives the actual location-scoped filtering applied by the viewer
  // (Occupied/Empty totals and slot highlighting scoped to the receipt) (#912).
  // PHR items have no `qty` field (one row per issued asset), so each counts
  // as 1 toward the total; THR items retain their existing qty-based count.
  const handReceiptContext: FrontWindowShelfMapHandReceiptContext | null =
    (thrItems.length > 0 || phrItems.length > 0) && currentCustomer
      ? {
          customerName: currentCustomer.display_name ?? null,
          thrItemCount: thrItems.reduce((sum, item) => sum + (item.qty ?? 0), 0) + phrItems.length,
          lins: Array.from(
            new Set(
              [...thrItems, ...phrItems]
                .map((item) => item.lin?.trim())
                .filter((lin): lin is string => Boolean(lin)),
            ),
          ),
        }
      : null;
  const navigate = useNavigate();
  const params = useParams();
  const activeTabParam = params.activeTab as TabKey | undefined;

  // Service members and HR cannot view Transaction History (#1085 — the SOC
  // hand receipt items an hr user can view are already scoped server-side,
  // but the tab exposed the full unscoped transaction log); comptrollers
  // only see Allocation.
  const visibleTabKeys: TabKey[] = useMemo(
    () =>
      isComptroller
        ? tabKeys.filter((k) => k === 'allocation')
        : isServiceMember || isHr
          ? tabKeys.filter((k) => k !== 'transaction-history')
          : tabKeys,
    [isServiceMember, isComptroller, isHr],
  );

  const initialIndex =
    activeTabParam && visibleTabKeys.includes(activeTabParam)
      ? visibleTabKeys.indexOf(activeTabParam)
      : 0;
  const [tabIndex, setTabIndex] = useState(initialIndex);
  const [tabActions, setTabActions] = useState<React.ReactNode>(null);

  // Keep internal state in sync when URL param changes (e.g., back/forward navigation)
  useEffect(() => {
    if (activeTabParam && visibleTabKeys.includes(activeTabParam)) {
      const newIdx = visibleTabKeys.indexOf(activeTabParam);
      if (newIdx !== tabIndex) setTabIndex(newIdx);
    } else if (!activeTabParam && tabIndex !== 0) {
      setTabIndex(0);
    } else if (activeTabParam && !visibleTabKeys.includes(activeTabParam)) {
      // Tab not visible for this role — redirect to default
      navigate(routeForTab('allocation'), { replace: true });
      setTabIndex(0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTabParam, isServiceMember]);

  const handleChange = (_: React.SyntheticEvent, newValue: number) => {
    setTabIndex(newValue);
    const key = visibleTabKeys[newValue] ?? 'allocation';
    navigate(routeForTab(key), { replace: false });
  };

  // Ensure left/right arrow keys move selection when Tabs have focus
  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'ArrowRight' || e.key === 'ArrowLeft') {
      e.preventDefault();
      setTabIndex((prev) => {
        const delta = e.key === 'ArrowRight' ? 1 : -1;
        const next = (prev + visibleTabKeys.length + delta) % visibleTabKeys.length;
        const key = visibleTabKeys[next] ?? 'allocation';
        navigate(routeForTab(key));
        return next;
      });
    }
  };

  // Memoize context value to prevent infinite re-render loops in consumers
  const tabActionsContextValue = useMemo(() => ({ setActions: setTabActions }), []);

  return (
    <Card>
      <CardContent sx={{ pt: 2 }}>
        <Box sx={{ mb: 2 }}>
          <IemContextualActions />
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Tabs
            value={tabIndex}
            onChange={handleChange}
            onKeyDown={handleKeyDown}
            aria-label={iemLabels.main_content_tabs_label}
            variant="scrollable"
            allowScrollButtonsMobile
          >
            <Tab
              icon={<AllInboxOutlined fontSize="small" />}
              iconPosition="top"
              label="Allocation"
              {...a11yProps('allocation', 0)}
            />
            {!isComptroller && (
              <Tab
                icon={<Inventory2Outlined fontSize="small" />}
                iconPosition="top"
                label={ieKitTabLabel}
                {...a11yProps('ie', 1)}
              />
            )}
            {!isServiceMember && !isComptroller && !isHr && (
              <Tab
                icon={<HistoryOutlined fontSize="small" />}
                iconPosition="top"
                label="Transaction History"
                {...a11yProps('transaction-history', 2)}
              />
            )}
          </Tabs>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            {/* Warehouse Shelf Map is visible only to user and user/admin roles.
                Comptroller, HR, finance, service-member, viewer, and front-window-only
                roles are scoped to specific workflows and should not access the
                warehouse shelf map. */}
            {/* Warehouse Shelf Map: only user/user+admin role combinations (#330).
                isComptroller/isHr are checked explicitly (in addition to the
                hasRole('user') effective-role check) because those flags are
                derived from the raw role set and don't depend on the account
                also carrying 'frontwindow' — accounts assigned 'user' plus a
                comptroller/finance/hr role but no 'frontwindow' role would
                otherwise still evaluate hasRole('user') as true and leak the
                Shelf Map entry into Front Window (#1080). */}
            {hasRole('user') && !isServiceMember && !isComptroller && !isHr && (
              <FrontWindowShelfMapLauncher
                warehouseEntityId={resolvedWarehouseId}
                warehouseName={resolvedWarehouseName}
                handReceiptContext={handReceiptContext}
              />
            )}
            {tabActions}
          </Box>
        </Box>

        <TabActionsContext.Provider value={tabActionsContextValue}>
          <TabPanel value={tabIndex} index={0} name="allocation">
            <AllocationTab />
          </TabPanel>

          {!isComptroller && (
            <TabPanel value={tabIndex} index={1} name="ie">
              <IeTable onClearError={() => {}} />
            </TabPanel>
          )}

          {!isServiceMember && !isComptroller && !isHr && (
            <TabPanel value={tabIndex} index={2} name="transaction-history">
              <TransactionHistoryTab />
            </TabPanel>
          )}
        </TabActionsContext.Provider>
      </CardContent>
    </Card>
  );
};
